import {PatientsTherapiesFilters} from '@/modules/patients-therapies/components'
import {PatientsTherapiesFiltersSchema} from '@/modules/patients-therapies/schemas'
import {useAppForm} from '@cypro/ui/integrations/form'
import {Badge, Box, Heading, Text, HStack, Button} from '@chakra-ui/react'
import {useScanner} from '@/shared/integrations/scanner'
import {useNavigate} from '@tanstack/react-router'
import {useTranslate} from '@tolgee/react'
import {LuX} from 'react-icons/lu'
import {useFindLabelPrescriptions} from '@/modules/patients-therapies/api'
import {parseScanCode} from '@/modules/patients-therapies/scan-code'
import {toaster} from '@cypro/ui/toaster'
import {openPatientTherapySequenceDrawerById} from '@/modules/patients-therapies/components/patient-therapy-sequence-drawer/util'
import type {Patient} from '@cypro/engine/schema'

interface Props {
  actionsCount: number | undefined
  searchFilters: PatientsTherapiesFiltersSchema | undefined
  onNavigate: (filters: PatientsTherapiesFiltersSchema) => void
  onClearPatient: () => void
  selectedPatient: Patient | undefined
}

export function ActivityFilters({actionsCount, searchFilters, onNavigate, onClearPatient, selectedPatient}: Props) {
  const {t} = useTranslate('common')
  const navigate = useNavigate()
  const findLabelPrescriptions = useFindLabelPrescriptions()

  const searchForm = useAppForm({
    defaultValues: {
      search: searchFilters?.search || '',
      patientId: searchFilters?.patientId || '',
      status: searchFilters?.status || [],
      wards: searchFilters?.wards || [],
      sections: searchFilters?.sections || [],
      therapyDate: searchFilters?.therapyDate || [],
      onlyCurrentUserActions: Boolean(searchFilters?.onlyCurrentUserActions),
    } as PatientsTherapiesFiltersSchema,
    listeners: {
      onChange({formApi, fieldApi}) {
        if (fieldApi.name === 'search') return

        const {values} = formApi.state

        let status = values.status
        if (fieldApi.name === 'onlyCurrentUserActions' && fieldApi.state.value) {
          status = []
          formApi.setFieldValue('status', status)
        }

        let onlyCurrentUserActions = values.onlyCurrentUserActions
        if (fieldApi.name === 'status' && fieldApi.state.value?.length) {
          onlyCurrentUserActions = false
          formApi.setFieldValue('onlyCurrentUserActions', onlyCurrentUserActions)
        }

        onNavigate({
          ...values,
          status,
          onlyCurrentUserActions,
        })
      },
    },
  })

  useScanner(null, {
    onComplete(value) {
      void onScan(value)
    },
    global: true,
  })

  async function onScan(value: string) {
    try {
      const code = parseScanCode(value)

      if (!code) {
        toaster.error({title: 'Invalid code scanned'})
        return
      }
      if (code.type === 'sequence') {
        openPatientTherapySequenceDrawerById(code.id)
        return
      }
      if (code.type === 'patient') {
        navigate({
          to: '/activity',
          from: '/activity/',
          search: prev => ({...prev, filters: {...prev.filters, patientId: code.id}}),
        })
        return
      }

      // Preparation label: open the prescription only when the code points at exactly one.
      const [match, ...others] = await findLabelPrescriptions(code)
      if (!match) {
        toaster.error({title: t('scanned_label_not_found', 'No prescription matches the scanned label')})
        return
      }
      if (others.length) {
        toaster.error({
          title: t('scanned_label_ambiguous', 'Several prescriptions match the scanned label; open it from the list'),
        })
        return
      }
      openPatientTherapySequenceDrawerById(match.id)
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <form onSubmit={e => e.preventDefault()}>
      <searchForm.AppForm>
        <Box
          display="flex"
          flexDirection={{base: 'column', md: 'row'}}
          justifyContent="space-between"
          width="100%"
          py={{base: '2', md: '3.5'}}
        >
          <Box display={{base: 'none', md: 'block'}}>
            <HStack gapX="2">
              <Heading fontSize="2xl">{t('activity')}</Heading>
              <Badge>{actionsCount ?? 0}</Badge>
            </HStack>
          </Box>

          <Box>
            <searchForm.AppField
              name="search"
              listeners={{
                onChange: ({fieldApi}) =>
                  navigate({
                    to: '/activity',
                    from: '/activity/',
                    search: prev => ({...prev, filters: {...prev.filters, search: fieldApi.state.value}}),
                  }),
                onChangeDebounceMs: 300,
              }}
            >
              {field => (
                <field.Searchbox
                  placeholder={t('search_patient_or_hospital_id')}
                  width="100%"
                  maxWidth={{base: 'none', md: '15rem'}}
                />
              )}
            </searchForm.AppField>
          </Box>
        </Box>
        <HStack
          justifyContent="space-between"
          width="100%"
          maxWidth={{base: '85vw', md: 'none'}}
          overflowX="auto"
          py={{base: '1.5', md: '3.5'}}
        >
          <PatientsTherapiesFilters form={searchForm} />
        </HStack>
        {selectedPatient && (
          <Box
            mt="2"
            mb="2"
            paddingY="2px"
            paddingX="3.5"
            bg="cyan.subtle"
            border="1px"
            borderColor="cyan.muted"
            borderStyle="solid"
            display="flex"
            flexDir="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" flexDir="column" justifyContent="center" alignItems="center">
              <Text fontSize="md" fontWeight="medium">
                {selectedPatient.fullName}
              </Text>
              <Text fontSize="sm" fontWeight="normal" color="fg.muted">
                {t('current_patient')}
              </Text>
            </Box>
            <Button variant={'ghost'} onClick={onClearPatient}>
              <LuX size="16px" />
            </Button>
          </Box>
        )}
      </searchForm.AppForm>
    </form>
  )
}
